import { Icon, Text, XStack, YStack } from '@frontend/web-react';
import { concatClasses, safeCss } from '@frontend/web-utils';
import { useClaimLowCreditBonusMutation } from '@frontend/web/hooks';
import { handleMutation } from '@frontend/web/utils';

type LowCreditBonusBannerProps = {
  dismiss: () => void;
  shouldShowBanner: boolean;
};

export const LowCreditBonusBanner = ({ dismiss, shouldShowBanner }: LowCreditBonusBannerProps) => {
  const [claimLowCreditBonus, { isLoading }] = useClaimLowCreditBonusMutation();

  if (!shouldShowBanner) return null;

  const handleClaim = () => {
    handleMutation({
      mutation: async () => {
        try {
          return await claimLowCreditBonus().unwrap();
        } catch (error) {
          const alreadyClaimed =
            typeof error === 'object' && error !== null && 'status' in error && error.status === 409;
          if (alreadyClaimed) {
            dismiss();
            return undefined;
          }
          throw error;
        }
      },
      onSuccess: (result) => {
        const claimSucceeded = result !== undefined;
        if (claimSucceeded) {
          dismiss();
        }
      },
      showSuccessToast: false,
    });
  };

  const handleDismiss = (e: React.MouseEvent) => {
    e.stopPropagation();
    dismiss();
  };

  const handleClaimKeyDown = (e: React.KeyboardEvent) => {
    if (isLoading) return;
    if (e.key === 'Enter') {
      e.preventDefault();
      handleClaim();
    }
  };

  return (
    <XStack
      className={safeCss('bg-blue-100 px-md py-md items-center gap-md fixed bottom-none right-none z-50')}
      style={{ left: 'var(--app-shell-navbar-width, 0px)' }}
    >
      <YStack className="flex-1">
        <Text isSelectable={false} type="h6">
          You&apos;re running low on credits.
        </Text>
        <Text isSelectable={false} type="p-sm">
          Claim a bonus to keep things moving.
        </Text>
      </YStack>
      <XStack
        aria-disabled={isLoading}
        className={concatClasses(
          safeCss('bg-neutral-950 px-md py-sm rounded-sm cursor-pointer transition-opacity'),
          {
            'hover:opacity-90': !isLoading,
            'opacity-50': isLoading,
          }
        )}
        onClick={isLoading ? undefined : handleClaim}
        onKeyDown={handleClaimKeyDown}
        role="button"
        tabIndex={isLoading ? -1 : 0}
      >
        <Text
          className="text-textInverse font-semibold"
          isSelectable={false}
          type="p-sm"
        >
          Claim bonus
        </Text>
      </XStack>
      <XStack className="p-xs cursor-pointer" onClick={handleDismiss}>
        <Icon name="IconX" size={20} />
      </XStack>
    </XStack>
  );
};
